class CarouselComponent extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('.flexible-carousel__track');
    this.slides = this.querySelectorAll('.flexible-carousel__slide');
    this.prevButton = this.querySelector('.flexible-carousel__arrow--prev');
    this.nextButton = this.querySelector('.flexible-carousel__arrow--next');
    this.dotsContainer = this.querySelector('.flexible-carousel__dots');

    if (!this.track || this.slides.length === 0) return;

    this.currentIndex = 0;
    this.isDragging = false;
    this.startX = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.animationID = null;
    this.autoplayInterval = null;
    this.isAutoplayPaused = false;

    this.init();
  }

  init() {
    // Calculate slides per view
    this.updateSlidesPerView();

    // Arrow navigation
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.prev());
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.next());
    }

    // Create dots if container exists
    if (this.dotsContainer) {
      this.createDots();
    }

    // Touch/drag events
    this.track.addEventListener('mousedown', this.dragStart.bind(this));
    this.track.addEventListener('touchstart', this.dragStart.bind(this), { passive: true });
    this.track.addEventListener('mouseup', this.dragEnd.bind(this));
    this.track.addEventListener('touchend', this.dragEnd.bind(this));
    this.track.addEventListener('mousemove', this.drag.bind(this));
    this.track.addEventListener('touchmove', this.drag.bind(this), { passive: false });
    this.track.addEventListener('mouseleave', this.dragEnd.bind(this));

    // Prevent context menu on long press
    this.track.addEventListener('contextmenu', (e) => {
      if (this.isDragging) e.preventDefault();
    });

    // Resize observer
    this.resizeObserver = new ResizeObserver(() => {
      this.updateSlidesPerView();
      this.goToSlide(this.currentIndex, false);
      this.updateArrows();
      if (this.dotsContainer) this.createDots();
    });
    this.resizeObserver.observe(this);

    // Initial state
    this.updateArrows();

    // Autoplay
    if (this.dataset.autoplay === 'true') {
      this.initAutoplay();
    }
  }

  initAutoplay() {
    const speed = parseInt(this.dataset.autoplaySpeed) || 5;
    const pauseOnHover = this.dataset.pauseOnHover !== 'false';

    this.startAutoplay(speed * 1000);

    if (pauseOnHover) {
      this.addEventListener('mouseenter', () => this.pauseAutoplay());
      this.addEventListener('mouseleave', () => this.resumeAutoplay());
      this.addEventListener('focusin', () => this.pauseAutoplay());
      this.addEventListener('focusout', () => this.resumeAutoplay());
    }
  }

  startAutoplay(interval) {
    this.autoplaySpeed = interval;
    this.autoplayInterval = setInterval(() => {
      if (!this.isAutoplayPaused) {
        this.nextAutoplay();
      }
    }, interval);
  }

  nextAutoplay() {
    if (this.currentIndex >= this.maxIndex) {
      this.goToSlide(0);
    } else {
      this.next();
    }
  }

  pauseAutoplay() {
    this.isAutoplayPaused = true;
  }

  resumeAutoplay() {
    this.isAutoplayPaused = false;
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  updateSlidesPerView() {
    const styles = getComputedStyle(this);
    const width = this.offsetWidth;

    if (width >= 990) {
      this.slidesPerView = parseInt(styles.getPropertyValue('--carousel-columns-desktop')) || 4;
    } else if (width >= 750) {
      this.slidesPerView = parseInt(styles.getPropertyValue('--carousel-columns-tablet')) || 3;
    } else {
      this.slidesPerView = parseInt(styles.getPropertyValue('--carousel-columns-mobile')) || 1;
    }

    this.maxIndex = Math.max(0, this.slides.length - this.slidesPerView);
  }

  getSlideWidth() {
    if (this.slides.length === 0) return 0;
    const slide = this.slides[0];
    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    return slide.offsetWidth + gap;
  }

  goToSlide(index, animate = true) {
    this.currentIndex = Math.max(0, Math.min(index, this.maxIndex));
    const slideWidth = this.getSlideWidth();
    const translateX = -this.currentIndex * slideWidth;

    this.track.style.transition = animate ? 'transform 0.4s ease' : 'none';
    this.track.style.transform = `translateX(${translateX}px)`;

    this.prevTranslate = translateX;
    this.currentTranslate = translateX;

    this.updateArrows();
    this.updateDots();
  }

  prev() {
    if (this.currentIndex > 0) {
      this.goToSlide(this.currentIndex - 1);
    }
  }

  next() {
    if (this.currentIndex < this.maxIndex) {
      this.goToSlide(this.currentIndex + 1);
    }
  }

  updateArrows() {
    if (this.prevButton) {
      if (this.currentIndex <= 0) {
        this.prevButton.setAttribute('disabled', '');
      } else {
        this.prevButton.removeAttribute('disabled');
      }
    }

    if (this.nextButton) {
      if (this.currentIndex >= this.maxIndex) {
        this.nextButton.setAttribute('disabled', '');
      } else {
        this.nextButton.removeAttribute('disabled');
      }
    }
  }

  // Dots
  createDots() {
    if (!this.dotsContainer) return;

    const totalDots = this.maxIndex + 1;
    this.dotsContainer.innerHTML = '';

    if (totalDots <= 1) {
      this.dotsContainer.style.display = 'none';
      return;
    }

    this.dotsContainer.style.display = '';

    for (let i = 0; i < totalDots; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'flexible-carousel__dot';
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      if (i === this.currentIndex) {
        dot.classList.add('flexible-carousel__dot--active');
      }
      dot.addEventListener('click', () => this.goToSlide(i));
      this.dotsContainer.appendChild(dot);
    }
  }

  updateDots() {
    if (!this.dotsContainer) return;

    const dots = this.dotsContainer.querySelectorAll('.flexible-carousel__dot');
    dots.forEach((dot, index) => {
      dot.classList.toggle('flexible-carousel__dot--active', index === this.currentIndex);
    });
  }

  // Touch/Drag functionality
  dragStart(event) {
    if (event.type === 'mousedown') {
      event.preventDefault();
    }

    this.isDragging = true;
    this.classList.add('is-dragging');
    this.startX = this.getPositionX(event);
    this.track.style.transition = 'none';

    this.animationID = requestAnimationFrame(this.animation.bind(this));
  }

  drag(event) {
    if (!this.isDragging) return;

    const currentX = this.getPositionX(event);
    const diff = currentX - this.startX;
    this.currentTranslate = this.prevTranslate + diff;

    // Prevent scrolling when swiping horizontally
    if (event.type === 'touchmove' && Math.abs(diff) > 10) {
      event.preventDefault();
    }
  }

  dragEnd() {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.classList.remove('is-dragging');
    cancelAnimationFrame(this.animationID);

    const diff = this.currentTranslate - this.prevTranslate;
    const threshold = this.getSlideWidth() / 4;

    if (diff < -threshold && this.currentIndex < this.maxIndex) {
      this.goToSlide(this.currentIndex + 1);
    } else if (diff > threshold && this.currentIndex > 0) {
      this.goToSlide(this.currentIndex - 1);
    } else {
      this.goToSlide(this.currentIndex);
    }
  }

  getPositionX(event) {
    return event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
  }

  animation() {
    if (this.isDragging) {
      // Apply boundaries
      const maxTranslate = 0;
      const minTranslate = -this.maxIndex * this.getSlideWidth();

      // Add resistance at edges
      if (this.currentTranslate > maxTranslate) {
        this.currentTranslate = maxTranslate + (this.currentTranslate - maxTranslate) * 0.3;
      } else if (this.currentTranslate < minTranslate) {
        this.currentTranslate = minTranslate + (this.currentTranslate - minTranslate) * 0.3;
      }

      this.track.style.transform = `translateX(${this.currentTranslate}px)`;
      this.animationID = requestAnimationFrame(this.animation.bind(this));
    }
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.animationID) {
      cancelAnimationFrame(this.animationID);
    }
    this.stopAutoplay();
  }
}

customElements.define('carousel-component', CarouselComponent);
